import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import { renderYesNoField, renderRangeField, renderSelectField, renderCheckboxGroup, renderInputTextField, validateFields, handleConfirmSave, handleNavigateBack } from "components/formUtils";

// Função para converter campos Sim/Não para strings
const convertYesNoFieldsToString = (data, yesNoFields) => {
  const updatedData = { ...data };
  yesNoFields.forEach(field => {
    if (data[field] === true || data[field] === 1) {
      updatedData[field] = "1"; // Sim
    } else {
      updatedData[field] = "0"; // Não
    }
  });
  return updatedData;
};

const IncisaoForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();

  const yesNoFields = ['mediana', 'conversao_esternotomia']; // Campos Sim/Não

  const [formData, setFormData] = useState({
    localizacao: "",
    mediana: "",
    tipo_esternotomia: "",
    lateralidade: "",
    posicao: "",
    tamanho_incisao: "",
    categoria_tamanho: "",
    numero_incisao_adicional: "",
    incisao_trabalho_pele: [],
    espaco_intercostal_aberto: [],
    conversao_esternotomia: "",
    motivo_conversao: "",
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_API_URL}/incisao/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Converte os campos de Sim/Não para strings e garante o correto carregamento de checkboxes
            const convertedData = convertYesNoFieldsToString(data.data, yesNoFields);
            convertedData.incisao_trabalho_pele = data.data.incisao_trabalho_pele || [];
            convertedData.espaco_intercostal_aberto = data.data.espaco_intercostal_aberto || [];
            setFormData(convertedData);
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar os dados",
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar os dados",
          });
        });
    }
  }, [id, token, notify]);

  const requiredFields = ["localizacao", "mediana", "tamanho_incisao"];
  const apiUrl = id
    ? `${process.env.REACT_APP_API_URL}/incisao/update/${id}`
    : `${process.env.REACT_APP_API_URL}/incisao/add`;

  const handleSubmit = () => {
    const method = id ? "PUT" : "POST";
    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Incisão atualizada com sucesso!" : "Incisão adicionada com sucesso!",
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`,
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados",
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Função para lidar com checkbox
  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const updatedValues = checked
        ? [...prev[field], value] // Adiciona se estiver checado
        : prev[field].filter((v) => v !== value); // Remove se desmarcado
      return { ...prev, [field]: updatedValues };
    });
  };

  const confirmSave = handleConfirmSave(formData, requiredFields, validateFields, GetTranslation, notify, handleSubmit);
  const handleBack = handleNavigateBack(navigate, id);

  return (
    <div className="card p-3">
      <ConfirmDialog />
      <h4>{id ? GetTranslation("Edit_Incisao") : GetTranslation("Add_Incisao")}</h4>
      <form className="row g-3">
        {/* Localização */}
        {renderSelectField(
          formData,
          handleChange,
          "localizacao",
          "Localização",
          [
            "Esternotomia", "Miniesternotomia em J", "Miniesternotomia em L", 
            "Toracotomia anterior direita", "Toracotomia axilar direita", 
            "Toracotomia periareolar direita / MAMICS", "Toracotomia anterolateral esquerda", 
            "Toracotomia lateral esquerda"
          ],
          GetTranslation,
          false,
          true // Campo obrigatório
        )}

        {/* Mediana */}
        {renderYesNoField(formData, handleChange, "mediana", "Mediana", GetTranslation)}

        {/* Tamanho da incisão */}
        {renderRangeField(formData, setFormData, "tamanho_incisao", "Tamanho da incisão", 0, 10, 2, GetTranslation, "(cm)")}

        {/* Número de incisões adicionais */}
        {renderRangeField(formData, setFormData, "numero_incisao_adicional", "Número de incisões adicionais", 0, 20, 0, GetTranslation)}

        {/* Tipo de esternotomia (se mediana) */}
        {formData.mediana === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "tipo_esternotomia",
            "Tipo de esternotomia",
            [
              "Sem esternotomia", "Completa/Convencional", "Parcial em J", "Parcial em L", 
              "Parcial em T invertido", "Parcial em T", "H deitado"
            ],
            GetTranslation
          )}

        {/* Lateralidade e posição (se não mediana) */}
        {formData.mediana === "0" && (
          <>
            {renderSelectField(
              formData,
              handleChange,
              "lateralidade",
              "Lateralidade",
              ["Esquerda", "Direita"],
              GetTranslation
            )}

            {renderSelectField(
              formData,
              handleChange,
              "posicao",
              "Posição",
              ["Anterior", "Anterolateral", "Lateral/Axilar"],
              GetTranslation
            )}
          </>
        )}

        {/* Categoria do tamanho */}
        {formData.tamanho_incisao && (
          <>
            <div className="col-md-6">
              <label className="form-label">{GetTranslation("Categoria do tamanho")}</label>
              <p className="form-control-static">
                {formData.tamanho_incisao <= 2
                  ? GetTranslation("Endoscópica <= 2cm")
                  : formData.tamanho_incisao <= 4
                  ? GetTranslation("Microincisão 2-4cm")
                  : formData.tamanho_incisao <= 6
                  ? GetTranslation("Miniincisão 4-6cm")
                  : GetTranslation("Convencional >= 6cm")}
              </p>
            </div>
            {/* Envio do valor da categoria como hidden */}
            <input type="hidden" name="categoria_tamanho" value={formData.categoria_tamanho} />
          </>
        )}

        {/* Incisão de trabalho na pele (se não mediana) */}
        {formData.mediana === "0" &&
          renderCheckboxGroup(
            formData,
            handleCheckboxChange,
            "incisao_trabalho_pele",
            "Incisão de trabalho na pele",
            ["1º EIC", "2º EIC", "3º EIC", "4º EIC", "5º EIC", "6º EIC", "7º EIC", "8º EIC"],
            GetTranslation
          )}

        {/* Espaço intercostal aberto (se não mediana) */}
        {formData.mediana === "0" &&
          renderCheckboxGroup(
            formData,
            handleCheckboxChange,
            "espaco_intercostal_aberto",
            "Espaço intercostal aberto",
            ["1º EIC", "2º EIC", "3º EIC", "4º EIC", "5º EIC", "6º EIC", "7º EIC", "8º EIC"],
            GetTranslation
          )}

        {/* Conversão para esternotomia */}
        {renderYesNoField(formData, handleChange, "conversao_esternotomia", "Conversão para esternotomia", GetTranslation)}

        {/* Motivo da conversão (se houve conversão) */}
        {formData.conversao_esternotomia === "1" &&
          renderInputTextField(formData, handleChange, "motivo_conversao", "Motivo da conversão", GetTranslation)}

        <div className="col-12 d-flex justify-content-between">
          <Button label={GetTranslation("Voltar")} className="btn btn-secondary" onClick={handleBack} />
          <Button label={id ? GetTranslation("Update") : GetTranslation("Create")} className="btn btn-primary" type="submit" onClick={confirmSave} />
        </div>
      </form>
    </div>
  );
};

export default IncisaoForm;
